import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Image } from 'react-bootstrap';
import { formatPrice, sanitizeString } from '../Functions';
import filterResetIcon from '../assets/images/filter-reset.svg';
import '../assets/scss/components/custom-form.scss';
import '../assets/scss/components/filter.scss';

function PropertyFilter({ filters, onFilterChange, hero = null }) {
    const navigate = useNavigate();
    const filterDefault = { // Using snake case to match API fields
        property_type: '',
        land_use: '',
        bedrooms: '',
        price: '',
        city: '',
        payment_plan: '',
    }
    const [localFilters, setLocalFilters] = useState(filters || filterDefault);

    // Separate state for the formatted price
    const [displayPrice, setDisplayPrice] = useState(formatPrice(filters?.price));

    const numOfFilterCols = hero ? 4 : 6;

    // Update local filters when prop changes
    useEffect(() => {
        setLocalFilters(filters);
        setDisplayPrice(formatPrice(filters?.price));
    }, [filters]);

    // Handle change to filter fields
    const handleChange = (e) => {
        const { name, value } = e.target;

        setLocalFilters((prevState) => ({
            ...prevState,
            bedrooms: name === 'land_use' ? '' : prevState.bedrooms, // Reset bedrooms if property type is land
            land_use: name === 'bedrooms' ? '' : prevState.land_use, // Reset land_use if property type is building
            [name]: value,
        }));
    };

    // Handle change to price field
    const handlePriceChange = (e) => {
        const value = e.target.value;
        const sanitizedValue = sanitizeString(value);

        // Update the display with the formatted value
        setDisplayPrice((formatPrice(sanitizedValue)));

        // Update the state with the clean, numeric value
        setLocalFilters((prevState) => ({
            ...prevState,
            price: sanitizedValue,
        }));
    };

    const clearFilters = () => {
        setLocalFilters(filterDefault);
        setDisplayPrice('');
        navigate('/properties'); // Navigate back to a clean URL
    }

    // Handle submit event
    const handleSubmit = (e) => {
        e.preventDefault();

        if (onFilterChange) {
            onFilterChange(localFilters); // Only Listings page needs FilterChange
        }
        navigate("/properties", { state: { filters: localFilters } });
    }

    return (
        <Form onSubmit={ handleSubmit } className="filter py-sm-3 py-lg-0 py-xl-0 py-xxl-0 mx-auto">
            {/* Split view in hero section */}
            { hero &&
                <>
                    {/* Property type and Size */}
                    <Row className="top-field pt-lg-3 pt-xl-3 pt-xxl-3" xs={1} sm={2} md={2} lg={2} xl={2} xxl={2}>
                        <Form.Group controlId="property-type" as={Col} className="pb-3 mb-2">
                            <Form.Label>Looking For</Form.Label>
                            <Form.Select name="property_type" value={ localFilters.property_type } onChange={ handleChange }>
                                <option value="">Property Type</option>
                                <option value="Fully-Detached">Fully Detached</option>
                                <option value="Semi-Detached">Semi-Detached</option>
                                <option value="Terrace">Terrace Duplex</option>
                                <option value="Apartment">Apartment</option>
                                <option value="Bungalow">Bungalow</option>
                                <option value="Land">Land</option>
                                <option value="Office">Office</option>
                            </Form.Select>
                        </Form.Group>

                        { localFilters.property_type === "Land" ?
                            <Form.Group controlId="land-use" as={Col} className="pb-3 mb-2">
                                <Form.Label>Land Use</Form.Label>
                                <Form.Select name="land_use" className="px-3" value={ localFilters.land_use } onChange={ handleChange }>
                                    <option value="">Land Use</option>
                                    <option value="RESIDENTIAL">Residential</option>
                                    <option value="COMMERCIAL">Commercial</option>
                                    <option value="MIXED USE">Mixed Use</option>
                                </Form.Select>
                            </Form.Group>
                            :
                            <Form.Group controlId="bedrooms" as={Col} className="pb-3 mb-2">
                                <Form.Label>Bedrooms</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="bedrooms"
                                    min={1}
                                    placeholder="No. of bedrooms"
                                    value={ localFilters.bedrooms }
                                    onChange={ handleChange }
                                />
                            </Form.Group>
                        }
                    </Row>
                </>
            }

            {/* Budget, Location and Payment plan */}
            <Row className="bottom-field pt-lg-3 pt-xl-3 pt-xxl-3" xs={1} sm={2} md={2} lg={numOfFilterCols} xl={numOfFilterCols} xxl={numOfFilterCols}>
                { !hero &&
                    <>
                        <Form.Group controlId="property-type" as={Col} className="pb-3 mb-2">
                            <Form.Label>Looking For</Form.Label>
                            <Form.Select name="property_type" value={ localFilters.property_type } onChange={ handleChange }>
                                <option value="">Property Type</option>
                                <option value="Fully-Detached">Fully Detached</option>
                                <option value="Semi-Detached">Semi-Detached</option>
                                <option value="Terrace">Terrace Duplex</option>
                                <option value="Apartment">Apartment</option>
                                <option value="Bungalow">Bungalow</option>
                                <option value="Land">Land</option>
                                <option value="Office">Office</option>
                            </Form.Select>
                        </Form.Group>

                        { localFilters.property_type === "Land" ?
                            <Form.Group controlId="land-use" as={Col} className="pb-3 mb-2">
                                <Form.Label>Land Use</Form.Label>
                                <Form.Select name="land_use" className="px-3" value={ localFilters.land_use } onChange={ handleChange }>
                                    <option value="">Land Use</option>
                                    <option value="RESIDENTIAL">Residential</option>
                                    <option value="COMMERCIAL">Commercial</option>
                                    <option value="MIXED USE">Mixed Use</option>
                                </Form.Select>
                            </Form.Group>
                            :
                            <Form.Group controlId="bedrooms" as={Col} className="pb-3 mb-2">
                                <Form.Label>Bedrooms</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="bedrooms"
                                    min={1}
                                    placeholder="No. of bedrooms"
                                    value={ localFilters.bedrooms }
                                    onChange={ handleChange }
                                />
                            </Form.Group>
                        }
                    </>
                }
                {/* Budget */}
                <Form.Group controlId="budget" as={Col} className="pb-3 mb-2">
                    <Form.Label>Budget</Form.Label>
                    <Form.Control
                        type="text"
                        name="price"
                        placeholder="Max. Price"
                        value={ displayPrice }
                        onChange={ handlePriceChange }
                    />
                </Form.Group>

                {/* Location */}
                <Form.Group controlId="location" as={Col} className="pb-3 mb-2">
                    <Form.Label>Location</Form.Label>
                    <Form.Select name="city" value={ localFilters.city } onChange={ handleChange }>
                        <option value="">Cities</option>
                        <option value="Ajah">Ajah</option>
                        <option value="VGC">VGC</option>
                        <option value="Ikota">Ikota</option>
                        <option value="Orchid">Orchid</option>
                        <option value="Chevron">Chevron</option>
                        <option value="Ikate-Lekki">Ikate-Lekki</option>
                        <option value="VI">VI</option>
                        <option value="Ikoyi">Ikoyi</option>
                        <option value="Epe">Epe</option>
                        <option value="Lagos Mainland">Lagos Mainland</option>
                        <option value="Ibadan">Ibadan</option>
                        <option value="Port-Harcourt">Port-Harcourt</option>
                        <option value="Wuse">Wuse</option>
                        <option value="Others">Others</option>
                    </Form.Select>
                </Form.Group>

                {/* Payment plan */}
                <Form.Group controlId="payment-plan" as={Col} className="pb-3 mb-2">
                    <Form.Label>Payment Plan</Form.Label>
                    <Form.Select name="payment_plan" className="px-3" value={ localFilters.payment_plan } onChange={ handleChange }>
                        <option value="">Payment Plan</option>
                        <option value="Outright">Outright</option>
                        <option value="Off plan">Off plan</option>
                        <option value="3 Months">--3 months</option>
                        <option value="6 Months">--6 months</option>
                        <option value="9 Months">--9 months</option>
                        <option value="12 Months">--12 months</option>
                        <option value="18 Months">--18 months</option>
                        <option value="24 Months">--24 months</option>
                    </Form.Select>
                </Form.Group>

                {/* Search button */}
                <Col className="m-auto d-flex flex-row">
                    <Button className="flex-grow-1 me-2" type="submit">Search</Button>
                    <Button
                        className="w-auto d-flex items-center justify-content-center"
                        onClick={clearFilters}
                        style={{ background: "none"}}
                        title="Clear filter"
                    >
                        <Image className="p-1" src={filterResetIcon} />
                    </Button>
                </Col>
            </Row>
        </Form>
    )
}

export default PropertyFilter;
